import { useEffect, useState } from 'react';
import type {
  PropiedadResumen,
  PruebaIa,
  ResumenMes,
  UsuarioEquipo,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
}

/**
 * IA E INFORMES.
 *
 * Esta pantalla hace DOS cosas, y las dos son las que el equipo pidió:
 *
 * 1. Escribir el resumen del mes que lee el propietario en su portal. Se elige
 *    la casa, el mes y el idioma, se genera, se lee, se corrige si hace falta y
 *    se publica. El resumen es un documento con dueño, no una respuesta
 *    improvisada mientras el cliente carga la página.
 *
 * 2. Descargar los informes de actividad del mes: el de cada persona —horas,
 *    visitas, incidencias resueltas y cálculo de pago— y el de la cartera.
 *
 * Lo que había antes —las anomalías de gasto y el clasificador de incidencias—
 * se ha quitado. Ninguna de las dos usaba IA (una era estadística sobre el
 * propio historial, la otra una heurística de palabras) y estar bajo un título
 * que dice «IA» hacía suponer lo contrario. Suponerlo lleva a confiar en un
 * número que nadie ha comprobado.
 */

const MESES_CLAVE = [
  'mes.enero', 'mes.febrero', 'mes.marzo', 'mes.abril', 'mes.mayo', 'mes.junio',
  'mes.julio', 'mes.agosto', 'mes.septiembre', 'mes.octubre', 'mes.noviembre', 'mes.diciembre',
];

const IDIOMAS: Array<'es' | 'en' | 'de'> = ['es', 'en', 'de'];

export function Ia({ idioma, onVolver }: Props) {
  const ahora = new Date();
  /*
    ARRANCA EN EL MES PASADO, NO EN EL ACTUAL.

    El resumen se escribe sobre un mes CERRADO: el de este mes cambiaria cada
    dia hasta que acabe. Abrir en el mes en curso invita a publicar un resumen
    que ya no sera cierto pasado manana.
  */
  const inicio = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth() - 1, 1));
  const [anio, setAnio] = useState(inicio.getUTCFullYear());
  const [mes, setMes] = useState(inicio.getUTCMonth() + 1);

  const [propiedades, setPropiedades] = useState<PropiedadResumen[]>([]);
  const [personas, setPersonas] = useState<UsuarioEquipo[]>([]);
  const [resumenes, setResumenes] = useState<ResumenMes[] | null>(null);
  const [ia, setIa] = useState<{ disponible: boolean; proveedor: string | null } | null>(null);
  const [prueba, setPrueba] = useState<PruebaIa | null>(null);

  const [propiedadId, setPropiedadId] = useState('');
  const [idiomaResumen, setIdiomaResumen] = useState<'es' | 'en' | 'de'>('es');
  const [generando, setGenerando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [borrador, setBorrador] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.propiedades().then((r) => setPropiedades(r.propiedades)).catch(() => undefined);
    api.equipo().then((r) => setPersonas(r.equipo)).catch(() => undefined);
    api.iaEstado().then((r) => setIa(r.ia)).catch(() => undefined);
  }, []);

  const cargar = () => {
    setResumenes(null);
    api
      .resumenesMes(anio, mes)
      .then((r) => setResumenes(r.resumenes))
      .catch(() => setResumenes([]));
  };

  useEffect(cargar, [anio, mes]);

  const fallo = (e: unknown) =>
    setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));

  const generar = () => {
    if (!propiedadId) return;
    setGenerando(true);
    setError(null);
    api
      .generarResumenMes({ propiedadId, anio, mes, idioma: idiomaResumen })
      .then(cargar)
      .catch(fallo)
      .finally(() => setGenerando(false));
  };

  const guardarEdicion = (id: string) => {
    setError(null);
    api
      .editarResumenMes(id, borrador)
      .then(() => {
        setEditando(null);
        cargar();
      })
      .catch(fallo);
  };

  const anios = [ahora.getUTCFullYear(), ahora.getUTCFullYear() - 1, ahora.getUTCFullYear() - 2];

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'ia.titulo')}</h1>
          <p>{t(idioma, 'ia.sub')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* --- El mes del que se habla ---------------------------------------- */}
      <section className="tarjeta">
        <div className="fila-filtros">
          <label className="campo">
            <span className="etiqueta-campo">{t(idioma, 'ia.anio')}</span>
            <select value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
              {anios.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta-campo">{t(idioma, 'ia.mes')}</span>
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {MESES_CLAVE.map((clave, i) => (
                <option key={clave} value={i + 1}>
                  {t(idioma, clave)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* --- Resúmenes del portal del cliente -------------------------------- */}
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>{t(idioma, 'ia.resumenesTitulo')}</h2>
          {ia && (
            <span className={`pastilla ${ia.disponible ? 'verde' : 'neutra'}`}>
              {ia.disponible
                ? (ia.proveedor ?? t(idioma, 'ia.conectada'))
                : t(idioma, 'ia.sinProveedor')}
            </span>
          )}
        </div>
        <p className="suave">{t(idioma, 'ia.resumenesIntro')}</p>

        {ia && !ia.disponible && (
          <p className="error" role="alert">
            {t(idioma, 'ia.sinProveedorNota')}
          </p>
        )}

        <div className="fila-filtros">
          <label className="campo crecer">
            <span className="etiqueta-campo">{t(idioma, 'ia.propiedad')}</span>
            <select value={propiedadId} onChange={(e) => setPropiedadId(e.target.value)}>
              <option value="">{t(idioma, 'ia.eligePropiedad')}</option>
              {propiedades.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta-campo">{t(idioma, 'ia.idiomaResumen')}</span>
            <select
              value={idiomaResumen}
              onChange={(e) => setIdiomaResumen(e.target.value as 'es' | 'en' | 'de')}
            >
              {IDIOMAS.map((i) => (
                <option key={i} value={i}>
                  {t(idioma, `idioma.${i}`)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={generar}
            disabled={!propiedadId || generando || !(ia?.disponible ?? false)}
          >
            {generando ? t(idioma, 'ia.generando') : t(idioma, 'ia.generar')}
          </button>
        </div>

        {resumenes === null && <p>{t(idioma, 'app.cargando')}</p>}
        {resumenes !== null && resumenes.length === 0 && (
          <p className="suave">{t(idioma, 'ia.sinResumenes')}</p>
        )}

        {resumenes !== null && resumenes.length > 0 && (
          <div className="lista">
            {resumenes.map((r) => (
              <article key={r.id} className="resumen-mes">
                <div className="resumen-mes-cabeza">
                  <span className="crecer">
                    <b>{r.propiedadNombre}</b>
                    <small>
                      {t(idioma, `idioma.${r.idioma}`)}
                      {r.modelo ? ` · ${r.modelo}` : ` · ${t(idioma, 'ia.escritoAMano')}`}
                    </small>
                  </span>
                  <span className={`pastilla ${r.estado === 'PUBLICADO' ? 'verde' : 'ambar'}`}>
                    {t(idioma, `ia.estado.${r.estado}`)}
                  </span>
                </div>

                {editando === r.id ? (
                  <>
                    <textarea
                      rows={5}
                      value={borrador}
                      onChange={(e) => setBorrador(e.target.value)}
                    />
                    <div className="acciones">
                      <button type="button" onClick={() => guardarEdicion(r.id)}>
                        {t(idioma, 'ia.guardar')}
                      </button>
                      <button
                        type="button"
                        className="secundario"
                        onClick={() => setEditando(null)}
                      >
                        {t(idioma, 'obras.cancelar')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="resumen-mes-texto">{r.texto}</p>
                    <div className="acciones">
                      <button
                        type="button"
                        className="secundario"
                        onClick={() => {
                          setEditando(r.id);
                          setBorrador(r.texto);
                        }}
                      >
                        {t(idioma, 'ia.editar')}
                      </button>
                      {r.estado === 'BORRADOR' ? (
                        <button
                          type="button"
                          onClick={() =>
                            api.publicarResumenMes(r.id).then(cargar).catch(fallo)
                          }
                        >
                          {t(idioma, 'ia.publicar')}
                        </button>
                      ) : (
                        /*
                          Retirar hace falta: si un resumen sale con un dato mal,
                          la primera reaccion tiene que poder ser quitarlo, no
                          corregirlo con prisa mientras el dueno lo lee.
                        */
                        <button
                          type="button"
                          className="secundario"
                          onClick={() => api.retirarResumenMes(r.id).then(cargar).catch(fallo)}
                        >
                          {t(idioma, 'ia.retirar')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* --- Informes del mes ------------------------------------------------ */}
      <section className="tarjeta">
        <h2>{t(idioma, 'ia.informesTitulo')}</h2>
        <p className="suave">{t(idioma, 'ia.informesIntro')}</p>

        <div className="lista">
          <div className="fila">
            <Icono nombre="documento" />
            <span className="crecer">
              <b>{t(idioma, 'ia.informeCartera')}</b>
              <small>{t(idioma, 'ia.informeCarteraNota')}</small>
            </span>
            <a
              className="enlace"
              href={api.urlInformeCartera(anio, mes)}
              target="_blank"
              rel="noreferrer"
            >
              {t(idioma, 'ia.descargar')}
            </a>
          </div>

          {personas.map((p) => (
            <div key={p.id} className="fila">
              <Icono nombre="personas" />
              <span className="crecer">
                <b>{p.nombre}</b>
                <small>{t(idioma, `rol.${p.rol}`)}</small>
              </span>
              <a
                className="enlace"
                href={api.urlInformePersona(p.id, anio, mes)}
                target="_blank"
                rel="noreferrer"
              >
                {t(idioma, 'ia.descargar')}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* --- Conexión -------------------------------------------------------- */}
      {/*
        Probar CUESTA una llamada de verdad al proveedor, por eso no se hace
        sola al abrir la pantalla. Se queda porque cuando un resumen no sale, lo
        primero que hay que poder descartar es que la conexión esté caída.
      */}
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>{t(idioma, 'ia.conexionTitulo')}</h2>
          <button
            type="button"
            className="secundario"
            onClick={() => api.iaProbar().then((r) => setPrueba(r.prueba)).catch(fallo)}
          >
            {t(idioma, 'ia.probar')}
          </button>
        </div>
        {prueba && (
          <p className={prueba.ok ? 'suave' : 'error'}>
            {prueba.ok ? t(idioma, 'ia.pruebaOk') : t(idioma, 'ia.pruebaFallo')}
            {prueba.detalle ? ` · ${prueba.detalle}` : ''}
          </p>
        )}
      </section>
    </main>
  );
}
